package us.laantech.admin.menu.clients.products

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.compose.rememberFilePickerLauncher
import kotlinx.coroutines.launch
import us.laantech.common.dto.RetailProductDTO
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.Department
import us.laantech.common.ui.DataTableView
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.util.parseCsvRecords
import us.laantech.common.util.toDollarStrFromCents

data class RetailProductsManagerUiState(
    val accountSub: String? = null,
    val companyBranch: CompanyBranch,
    val products: List<RetailProductDTO> = emptyList(),
    val fileText: String? = null,
    val showUploadProductsModal: Boolean = false,
    val csv: CSV? = null,
    val dataUploadProgress: Float = 0.1f,
    val numRows: Long = 0,
    val numRowsProcessed: Long = 0,
    val uploadError: String? = null,
    val departments: List<Department> = emptyList()
)

class CSV(private val fileText: String) {

    // Lazy: nothing is parsed until you iterate. Each record is produced on demand.
    // Quote-aware (RFC 4180) so a name containing a comma stays in one field.
    val rows: Sequence<List<String>>
        get() = parseCsvRecords(fileText)

    fun listHeaders(): List<String> = rows.firstOrNull() ?: emptyList()

    // Header skipped, still lazy.
    fun dataRows(): Sequence<List<String>> = rows.drop(1)
}


@Composable
fun RetailProductsManagerView(
    accountSub: String?,
    companyBranch: CompanyBranch,
    onDismiss: () -> Unit,
    viewModel: RetailProductsManagerViewModel = viewModel(
        key = companyBranch.uuid,
        factory = viewModelFactory {
            initializer { RetailProductsManagerViewModel(accountSub, companyBranch) }
        }
    ),
) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val scope = rememberCoroutineScope()
    val launcher = rememberFilePickerLauncher(
        type = FileKitType.File("csv")
    ) { file ->
        file ?: return@rememberFilePickerLauncher
        scope.launch { viewModel.loadFile(file) }
    }

    LaunchedEffect(Unit) {
        viewModel.loadDepartments()
        viewModel.loadProducts()
    }

    Column(Modifier.fillMaxSize().padding(8.dp)) {
        // Top bar: branch label on the left, actions on the right.
        Card(border = BorderStroke(1.dp, Color.Gray)) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    text = "Branch: ${companyBranch?.branchName ?: "—"}",
                    modifier = Modifier.weight(1f),
                )
                Button(onClick = { }) {
                    Text(text = "Refresh")
                }
                Button(onClick = { launcher.launch() }) {
                    Text(text = "Upload")
                }
                Button(onClick = onDismiss) {
                    Text(text = "Back")
                }

                Button(onClick = {viewModel.loadNextPage(true)}) {
                    Text(text = "Previous Page")
                }
                Button(onClick = {viewModel.loadNextPage()}) {
                    Text(text = "Next Page")
                }

            }
        }

        DataTableView(rows = uiState.products) {
            column("Title", 2f) { Text("${it.title}", Modifier.weight(2f)) }
            column("Barcode") { Text("${it.barcode}", Modifier.weight(1f)) }

            column("Price", 0.5f) {
                Text("$${it.priceCents?.toDollarStrFromCents()}", Modifier.weight(0.5f))
            }

            column("Department", 1f) {
                val departmentName = uiState.departments.find { dept ->
                    it.departmentUUID == dept.departmentUUID
                }?.departmentName
                Text(departmentName.orEmpty(), Modifier.weight(1f))
            }

            column("Age", 0.5f) { Text("${it.ageLimit}", Modifier.weight(0.5f)) }
            column("EBT", 0.5f) { Text("${it.ebt}", Modifier.weight(0.5f)) }
            column("Tax", 0.5f) { Text("${it.taxable}", Modifier.weight(0.5f)) }

            column("Action", 0.5f) {
                Button(onClick = { }, Modifier.weight(0.5f)) {
                    Text(text = "Edit")
                }
            }
        }


    }

    if (uiState.showUploadProductsModal) {
        UploadProductsModal(viewModel)
    }

}





